import { observable } from "mobx";

import { getServerTodayISO } from "@/utils/server-time.ts";
import { dateTimeISOString } from "@/utils/utils.ts";

import {
  apiPostClinicalStudiesEnroll,
  apiPostClinicalStudiesStudyVisit,
  apiPostPatients,
  apiPostPatientsPatientAndUser,
  ClinicalStudies,
  MeasurementRequiredData_Input,
  StudyVisitSchedule_Input,
  StudyVisitType,
} from "../../api";
import { ApiConnector } from "../../api/api-connector.ts";
import {
  hasAdminAccess,
  hasMonitoringAccess,
} from "../../constants/permissions";
import { DataAdd } from "../../models/data-add";
import { splitNameArray } from "../../utils/names";
import { navigate } from "../../utils/navigate";
import { loadPhysicians, Physicians } from "../admin/physicians/physicians";
import { Users } from "../admin/users/users";
import { fetchEnrollmentPrefill } from "../clinical-studies/utils";

const REMEMBERED_FIELDS_KEY = "addPatient.rememberedFields";
const REMEMBERED_FIELD_NAMES = [
  "race",
  "country",
  "region",
  "monitoring_type",
  "health_insurance",
] as const;
type RememberedFieldName = (typeof REMEMBERED_FIELD_NAMES)[number];
const RACE_DEFAULT = "Unknown / Not Reported";
// Fields with a non-null default that should be treated as "empty" for the
// purposes of remembering — i.e. don't persist or count them as user input.
const REMEMBERED_FIELD_DEFAULTS: Partial<Record<RememberedFieldName, unknown>> =
  {
    race: RACE_DEFAULT,
  };

export interface EnrollmentVisit {
  visit_date: string;
  visit_type: StudyVisitType;
}

export class AddPatient extends DataAdd<null> {
  @observable accessor createUser = false;
  @observable accessor rememberFields = false;
  @observable accessor dataUser = {
    preferred_language: "sk",
    token_expiry_timedelta: "P1D",
  };

  @observable accessor enrollInStudy = false;
  @observable accessor enrollment: {
    study_acronym: ClinicalStudies | null;
    enrollment_date: string;
    comment: string | null;
    schedule: StudyVisitSchedule_Input[];
  } = {
    study_acronym: null,
    enrollment_date: getServerTodayISO(),
    comment: null,
    schedule: [],
  };
  @observable accessor enrollmentVisits: EnrollmentVisit[] = [];
  @observable accessor prefillLoading = false;
  // Guards against out-of-order prefill responses: only the latest call's
  // result is applied. Plain (non-observable) — it's internal bookkeeping.
  prefillGen = 0;

  // constants
  constructor(api: ApiConnector) {
    super(api, null);
    this.addingList = {
      monitoring_type: null,
      date_of_birth: null,
      sex: null,
      height: null,
      description: null,
      country: null,
      region: null,
      race: RACE_DEFAULT,
      given_name: null,
      family_name: null,
      middle_names: null,
      name_particle: null,
      additional_family_names: null,
      name_suffix: null,
      title_before: null,
      title_after: null,
      name_order_given_first: true,
      residence: "N/A",
      health_insurance: null,
      id_number: null,
      email: null,
      phone: null,
      append_to_physicians: [],
      append_to_managing_users: [],
      self_measurement_config: [],
    };

    if (
      hasMonitoringAccess(this.api.permissions) ||
      hasAdminAccess(this.api.permissions)
    ) {
      const raw = localStorage.getItem(REMEMBERED_FIELDS_KEY);
      if (raw) {
        try {
          const remembered = JSON.parse(raw);
          let hasData = false;
          for (const field of REMEMBERED_FIELD_NAMES) {
            const value = remembered[field];
            if (
              value == null ||
              value === "" ||
              value === REMEMBERED_FIELD_DEFAULTS[field]
            ) {
              continue;
            }
            this.addingList[field] = value;
            hasData = true;
          }
          if (hasData) {
            this.rememberFields = true;
          } else {
            localStorage.removeItem(REMEMBERED_FIELDS_KEY);
          }
        } catch {
          localStorage.removeItem(REMEMBERED_FIELDS_KEY);
        }
      }
    }
  }

  /** Returns true if at least one field was actually written to localStorage. */
  persistRememberedFields(): boolean {
    if (!this.rememberFields) {
      localStorage.removeItem(REMEMBERED_FIELDS_KEY);
      return false;
    }
    const toRemember: Record<string, unknown> = {};
    for (const field of REMEMBERED_FIELD_NAMES) {
      const value = this.addingList[field];
      if (
        value == null ||
        value === "" ||
        value === REMEMBERED_FIELD_DEFAULTS[field]
      )
        continue;
      toRemember[field] = value;
    }
    if (Object.keys(toRemember).length === 0) {
      localStorage.removeItem(REMEMBERED_FIELDS_KEY);
      return false;
    }
    localStorage.setItem(REMEMBERED_FIELDS_KEY, JSON.stringify(toRemember));
    return true;
  }

  createPhysicians() {
    const physicians = new Physicians(
      [],
      this.api.permissions,
      this.api.requireUserUUID(),
    );
    void loadPhysicians(physicians);
    return physicians;
  }

  createUsers() {
    const users = new Users(this.api);

    void users.initPhysiciansOnly();

    return users;
  }

  resetEnrollment() {
    this.enrollment.study_acronym = null;
    this.enrollment.enrollment_date = getServerTodayISO();
    this.enrollment.comment = null;
    this.enrollment.schedule = [];
    this.enrollmentVisits = [];
  }

  async tryFetchPrefill() {
    if (
      !this.enrollInStudy ||
      !this.enrollment.study_acronym ||
      !this.enrollment.enrollment_date ||
      !this.addingList.country
    ) {
      return;
    }

    const gen = ++this.prefillGen;
    this.prefillLoading = true;
    try {
      const { schedule, visits } = await fetchEnrollmentPrefill(
        this.enrollment.study_acronym,
        this.enrollment.enrollment_date,
        this.addingList.country,
      );
      if (gen !== this.prefillGen) return;
      this.enrollment.schedule = schedule as any;
      this.enrollmentVisits = visits.map(
        (v): EnrollmentVisit => ({
          visit_date: v.visit_date,
          visit_type: v.visit_type as StudyVisitType,
        }),
      );
    } finally {
      if (gen === this.prefillGen) this.prefillLoading = false;
    }
  }

  async post() {
    let newPatId: number;
    const body = this.prepareBody();

    if (this.createUser) {
      const response = await apiPostPatientsPatientAndUser({
        body: {
          ...body,
          ...this.dataUser,
        },
      });
      newPatId = response.data.patient.patient_id;
    } else {
      const response = await apiPostPatients({ body });
      newPatId = response.data.patient_id;
    }

    if (this.enrollInStudy && this.enrollment.study_acronym) {
      const study = this.enrollment.study_acronym;
      const enrollRes = await apiPostClinicalStudiesEnroll({
        body: {
          patient_id: newPatId,
          study_acronym: study,
          enrollment_date: this.enrollment.enrollment_date,
          comment: this.enrollment.comment ?? undefined,
          schedule: this.enrollment.schedule.map((s) => ({
            ...s,
            scheduled_date:
              this.enrollmentVisits.find(
                (v) =>
                  v.visit_type === (s.visit_type as unknown as StudyVisitType),
              )?.visit_date ?? s.scheduled_date,
          })),
        },
      });
      const enrollmentUuid = enrollRes.data.uuid;

      await Promise.all(
        this.enrollmentVisits.map((v) =>
          apiPostClinicalStudiesStudyVisit({
            body: {
              patient_id: newPatId,
              study_acronym: study,
              study_enrollment_uuid: enrollmentUuid,
              visit_type: { visit_type: v.visit_type },
              visit_datetime: dateTimeISOString(v.visit_date),
            },
          }),
        ),
      );
    }

    navigate(`/?createdPatientId=${newPatId}`);
  }

  private prepareBody() {
    const config = this.addingList.self_measurement_config;
    return {
      ...this.addingList,
      middle_names: splitNameArray(this.addingList.middle_names),
      additional_family_names: splitNameArray(
        this.addingList.additional_family_names,
      ),
      self_measurement_config: config.length
        ? config.map((item: MeasurementRequiredData_Input, index: number) => ({
            ...item,
            order: index + 1,
          }))
        : undefined,
    };
  }
}
